import * as React from "react";

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { rankItem } from "@tanstack/match-sorter-utils";
import SoftBox from "components/UI/SoftBox";
import DataTableHeadCell from "./DataTableHeadCell";
import DataTableBodyCell from "./DataTableBodyCell";
import SoftTypography from "components/UI/SoftTypography";
import DebouncedFilter from "./DebouncedFilter";
import SoftButton from "../SoftButton";
import AutoCompleteOption from "../AutoCompleteOption";
import { ArrowBackIos, ArrowForwardIos, Download } from "@mui/icons-material";

const fuzzyFilter = (row, columnId, value, addMeta) => {
  const cellValue = row.getValue(columnId);

  // Konversi cellValue ke string untuk handle berbagai tipe data
  const cellValueStr =
    cellValue == null
      ? ""
      : typeof cellValue === "object"
        ? JSON.stringify(cellValue)
        : String(cellValue);

  // Jika tidak ada value, return true (tampilkan semua)
  if (!value) return true;

  const searchValue = String(value).toLowerCase().trim();
  const cellValueLower = cellValueStr.toLowerCase();

  // Untuk query > 2 karakter, gunakan substring match yang lebih akurat
  if (searchValue.length > 2) {
    return cellValueLower.includes(searchValue);
  }

  // Untuk query pendek (1-2 karakter), gunakan fuzzy untuk lebih fleksibel
  const itemRank = rankItem(cellValueStr, value);
  addMeta({
    itemRank,
  });
  return itemRank.passed;
};

const customGlobalFilter = (row, columnId, filterValue) => {
  if (!filterValue) return true;

  const searchTerm = String(filterValue).trim();

  // Detect pattern "column_name: value"
  const colonIndex = searchTerm.indexOf(":");
  if (colonIndex > 0) {
    const columnHint = searchTerm.substring(0, colonIndex).trim().toLowerCase();
    const searchValue = searchTerm
      .substring(colonIndex + 1)
      .trim()
      .toLowerCase();

    // Jika tidak ada value setelah ':', skip filter
    if (!searchValue) return true;

    // Get all cells dari row
    const allCells = row.getAllCells();

    // Cari kolom yang match dengan hint
    for (const cell of allCells) {
      const column = cell.column;
      const columnName =
        typeof column.columnDef.header === "string"
          ? column.columnDef.header.toLowerCase()
          : column.id.toLowerCase();

      // Check if column name matches the hint
      if (
        columnName.includes(columnHint) ||
        column.id.toLowerCase().includes(columnHint)
      ) {
        const cellValue = cell.getValue();
        const cellValueStr =
          cellValue == null
            ? ""
            : typeof cellValue === "object"
              ? JSON.stringify(cellValue)
              : String(cellValue);

        return cellValueStr.toLowerCase().includes(searchValue);
      }
    }

    // Jika tidak ada kolom yang match, return false
    return false;
  }

  // Global search di semua kolom (fallback ke behavior default)
  const searchLower = searchTerm.toLowerCase();
  const allCells = row.getAllCells();

  return allCells.some((cell) => {
    const cellValue = cell.getValue();
    const cellValueStr =
      cellValue == null
        ? ""
        : typeof cellValue === "object"
          ? JSON.stringify(cellValue)
          : String(cellValue);

    if (searchLower.length > 2) {
      return cellValueStr.toLowerCase().includes(searchLower);
    }

    const itemRank = rankItem(cellValueStr, searchTerm);
    return itemRank.passed;
  });
};

export default function SoftDataTable({
  table: { rows, columns },
  FilterComponent,
  withSearchFilter = true,
  withExport,
  exportFn,
  exportButtonProps,
  ...rest
}) {
  const [sorting, setSorting] = React.useState([]);
  const [globalFilter, setGlobalFilter] = React.useState("");
  const matchesMd = useMediaQuery("(max-width:600px)");

  // Server-side pagination: the parent owns page/pageSize and fetches only that slice.
  const manual = Boolean(rest.manualPagination);
  const currentPage = rest.page || 0;
  const currentPageSize = rest.pageSize || 10;

  const handlePaginationChange = (updater) => {
    const current = { pageIndex: currentPage, pageSize: currentPageSize };
    const next = typeof updater === "function" ? updater(current) : updater;

    if (next.pageSize !== current.pageSize) {
      rest.onPageSizeChange?.(next.pageSize);
      if (current.pageIndex !== 0) rest.onPageChange?.(0);
      return;
    }
    if (next.pageIndex !== current.pageIndex) {
      rest.onPageChange?.(next.pageIndex);
    }
  };

  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting,
      globalFilter,
      ...(manual
        ? { pagination: { pageIndex: currentPage, pageSize: currentPageSize } }
        : {}),
    },
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    manualPagination: manual,
    ...(manual
      ? {
          pageCount: rest.pageCount ?? -1,
          onPaginationChange: handlePaginationChange,
        }
      : {}),
    onGlobalFilterChange: manual ? undefined : setGlobalFilter,
    globalFilterFn: manual ? undefined : customGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: manual ? undefined : getFilteredRowModel(),
    getPaginationRowModel: manual ? undefined : getPaginationRowModel(),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!manual) table.setPageSize(rest?.pageStart || 5);
  }, []);

  return (
    <SoftBox
      sx={{
        "& .MuiTableRow-root:not(:last-child)": {
          "& td": {
            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
              `${borderWidth[1]} solid ${borderColor}`,
          },
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        px={!rest.noPaddingFilter && 3}
        sx={{ overflowX: "auto" }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <SoftTypography fontSize={14}>Show entries:</SoftTypography>
          <AutoCompleteOption
            noLabel
            options={["5", "10", "20", "30", "40", "50"]}
            value={[
              "",
              `${
                manual
                  ? currentPageSize
                  : table.getState().pagination.pageSize
              }`,
            ]}
            onSelect={(e, newVal) => {
              if (!newVal) return;
              if (manual) rest.onPageSizeChange?.(Number(newVal));
              else table.setPageSize(Number(newVal));
            }}
            width="30%"
          />
          {!matchesMd && (
            <SoftTypography fontSize={13}>
              Showing {`${table.getRowModel().rows.length}`} of{" "}
              {manual ? rest.totalRows ?? rows.length : rows.length} entries
            </SoftTypography>
          )}
        </Stack>
        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-end",
            alignItems: "center",
            my: matchesMd ? 2 : 1,
          }}
          spacing={1}
        >
          {withSearchFilter && (
            <DebouncedFilter
              value={manual ? rest.searchValue ?? "" : globalFilter}
              onChange={(value) => {
                if (manual) rest.onSearchChange?.(String(value));
                else setGlobalFilter(String(value));
              }}
              placeholder="Search all columns... (e.g., 'project manager: Andi')"
            />
          )}
          {withExport &&
            (!matchesMd ? (
              <SoftButton
                variant="outlined"
                onClick={exportFn}
                color="error"
                size="small"
                sx={{ p: 1, fontSize: 11 }}
                {...exportButtonProps}
              >
                Export Excel
              </SoftButton>
            ) : (
              <Tooltip title="Export Excel">
                <IconButton onClick={exportFn}>
                  <Download />
                </IconButton>
              </Tooltip>
            ))}
        </Stack>
      </Stack>
      <TableContainer
        sx={{
          overflowY: "auto",
          maxHeight: 600,
        }}
      >
        <Table id="myTable">
          <SoftBox
            component="thead"
            sx={{
              position: "sticky",
              top: 0,
              backgroundColor: "#fff",
              zIndex: 50,
            }}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <DataTableHeadCell
                    width="auto"
                    key={header.id}
                    sorted={header.column.getIsSorted()}
                    isSorted={header.column.getIsSorted()}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </DataTableHeadCell>
                ))}
              </TableRow>
            ))}
          </SoftBox>
          <TableBody>
            {rest.isLoading || table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <SoftTypography sx={{ fontSize: 13, textAlign: "center" }}>
                    {rest.isLoading ? "Loading Data ..." : "No Data Available"}
                  </SoftTypography>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                return (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <DataTableBodyCell
                          key={cell.id}
                          width={cell.column.columnDef?.width}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </DataTableBodyCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack
        direction="row"
        sx={{
          py: 2,
          px: 3,
          alignItems: "center",
          justifyContent: "flex-end",
          flex: 1,
          position: "sticky",
          bottom: 0,
          backgroundColor: "#fff",
        }}
        spacing={1}
      >
        <SoftButton
          sx={{ px: 1.5 }}
          variant="outlined"
          size="small"
          color="error"
          disabled={!table.getCanPreviousPage()}
          onClick={() => table.previousPage()}
          startIcon={<ArrowBackIos />}
        >
          Prev
        </SoftButton>
        <SoftButton
          sx={{ px: 1.5 }}
          variant="outlined"
          size="small"
          color="error"
          disabled={!table.getCanNextPage()}
          onClick={() => table.nextPage()}
          endIcon={<ArrowForwardIos />}
        >
          Next
        </SoftButton>
        <SoftTypography fontSize={14}>
          Page:{" "}
          <strong>
            {manual
              ? currentPage + 1
              : table.getState().pagination.pageIndex + 1}{" "}
            of {manual ? rest.pageCount || 1 : table.getPageCount()}
          </strong>
        </SoftTypography>
      </Stack>
    </SoftBox>
  );
}
